import React, { useCallback, useEffect, useRef, useState } from "react";
import { avsOsKbGlossary, avsOsKbSearch } from "../Services/services";
import {
  PAGE_SIZE,
  articleKey,
  glossaryParams,
  isOpenable,
  normaliseQuery,
  pageInfo,
  searchParams,
  topicLabel,
} from "../utils/kbGlossary";
import type { GlossaryData, KbArticleRef, SearchData } from "../utils/kbGlossary";

/**
 * The Knowledge Base page's body: a per-product glossary and a paged search over the same articles
 * (ACOP-146). No router in here, on purpose — the owner (KnowledgeBaseTwo) hands in `onOpenArticle`,
 * so this is unit-testable under react-scripts' Jest, which cannot resolve react-router.
 *
 * What a person sees is decided by AVS OS from their identity and roles, never here: a customer gets
 * the public KB; an employee gets the same until the Beta Preview toggle is ON (`includeInternal`),
 * which adds the internal KB, flask-marked. This component only asks.
 *
 * Internal articles are LISTED but not opened: they are not served by any viewer route yet (the
 * public article route 404s them by design, and the authenticated citation route denies kb_article),
 * so a link would land on "not in the knowledge base". An honest plain row beats a dead link.
 */

const SEARCH_DEBOUNCE_MS = 300;

const MESSAGES = {
  glossaryLoading: "Loading the glossary…",
  glossaryFailed: "The glossary could not be loaded right now. Please try again in a moment.",
  glossaryEmpty: "There are no knowledge base articles to show yet.",
  searchLoading: "Searching…",
  searchFailed: "The search could not be completed right now. Please try again in a moment.",
};

interface LoadState<T> {
  loading: boolean;
  error: boolean;
  data: T | null;
}

/** The standard envelope copilot-api wraps every proxy answer in: { status: 1, data }. */
function unwrap<T>(resp: { data?: { status?: number; data?: T } } | undefined): T | null {
  const body = resp?.data;
  return body?.status === 1 && body.data ? body.data : null;
}

function FlaskMark() {
  return (
    <i
      className="fa-solid fa-flask kb2-flask"
      title="Internal — employees only"
      aria-label="internal"
      data-testid="kb2-internal-mark"
    />
  );
}

interface ArticleRowProps {
  article: KbArticleRef;
  onOpenArticle: (key: string) => void;
  showTopic?: boolean;
}

function ArticleRow({ article, onOpenArticle, showTopic = false }: ArticleRowProps) {
  const openable = isOpenable(article);
  return (
    <li className="kb2-row" data-testid="kb2-row" data-id={article.id}>
      <div className="kb2-row-title">
        {openable ? (
          <button
            type="button"
            className="kb2-link"
            onClick={() => onOpenArticle(articleKey(article))}
            data-testid="kb2-title"
          >
            {article.title}
          </button>
        ) : (
          <span className="kb2-title-plain" data-testid="kb2-title-internal">
            {article.title}
          </span>
        )}
        {article.internal && <FlaskMark />}
      </div>
      {article.summary && <p className="kb2-row-summary">{article.summary}</p>}
      {showTopic && (
        <p className="kb2-row-meta" data-testid="kb2-meta">
          {topicLabel(article)}
          {article.products?.length > 0 && <> · {article.products.join(", ")}</>}
        </p>
      )}
      {article.internal && (
        <p className="kb2-row-meta">Internal article — not viewable here yet.</p>
      )}
    </li>
  );
}

interface PagerProps {
  total: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
}

function Pager({ total, page, pageSize, onPage }: PagerProps) {
  const info = pageInfo(total, page, pageSize);
  return (
    <nav className="kb2-pager" aria-label="Search result pages" data-testid="kb2-pager">
      <span className="kb2-pager-range" data-testid="kb2-range">
        {info.from}–{info.to} of {total}
      </span>
      <button
        type="button"
        className="kb2-btn"
        disabled={!info.hasPrev}
        onClick={() => onPage(info.current - 1)}
        data-testid="kb2-prev"
      >
        ← Previous
      </button>
      <span className="kb2-pager-page">Page {info.current} of {info.pages}</span>
      <button
        type="button"
        className="kb2-btn"
        disabled={!info.hasNext}
        onClick={() => onPage(info.current + 1)}
        data-testid="kb2-next"
      >
        Next →
      </button>
    </nav>
  );
}

interface KbBrowseProps {
  includeInternal?: boolean;
  onOpenArticle: (key: string) => void;
}

export default function KbBrowse({ includeInternal = false, onOpenArticle }: KbBrowseProps) {
  const [product, setProduct] = useState<string | null>(null);
  const [glossary, setGlossary] = useState<LoadState<GlossaryData>>({ loading: true, error: false, data: null });

  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState<LoadState<SearchData>>({ loading: false, error: false, data: null });

  // Which categories are open. Collapsed by default: ~200 titles in a handful of categories is a lot to scroll,
  // and it only grows. Lives here (not in the page) so opening an article and coming Back keeps it, because
  // KnowledgeBaseTwo keeps this view mounted.
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const glossaryReq = useRef(0);
  const searchReq = useRef(0);

  // ── Glossary: reload when the product or the internal toggle changes ────────────────────────
  useEffect(() => {
    const id = ++glossaryReq.current;
    setGlossary((g) => ({ ...g, loading: true, error: false }));
    (async () => {
      try {
        const data = unwrap<GlossaryData>(await avsOsKbGlossary(glossaryParams(product, includeInternal)));
        if (id !== glossaryReq.current) return;
        if (!data) {
          setGlossary({ loading: false, error: true, data: null });
          return;
        }
        setGlossary({ loading: false, error: false, data });
      } catch (err) {
        if (id !== glossaryReq.current) return;
        console.error("Failed to load the KB glossary:", err);
        setGlossary({ loading: false, error: true, data: null });
      }
    })();
  }, [product, includeInternal]);

  // ── Debounce what the reader types into the search box ──────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => {
      const next = normaliseQuery(input);
      setQuery((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [input]);

  // ── Search: whenever there is a phrase, the page, or the internal toggle ────────────────────
  useEffect(() => {
    if (!query) {
      searchReq.current++;
      setSearch({ loading: false, error: false, data: null });
      return;
    }
    const id = ++searchReq.current;
    setSearch((s) => ({ ...s, loading: true, error: false }));
    (async () => {
      try {
        const data = unwrap<SearchData>(await avsOsKbSearch(searchParams(query, page, includeInternal)));
        if (id !== searchReq.current) return;
        setSearch(data ? { loading: false, error: false, data } : { loading: false, error: true, data: null });
      } catch (err) {
        if (id !== searchReq.current) return;
        console.error("Failed to search the KB:", err);
        setSearch({ loading: false, error: true, data: null });
      }
    })();
  }, [query, page, includeInternal]);

  // A different product (or the internal toggle changing what is listed) is a different list: start collapsed.
  const listedProduct = glossary.data?.product ?? null;
  useEffect(() => {
    setExpanded(new Set());
  }, [listedProduct, includeInternal]);

  const toggleCategory = useCallback((category: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);

  const clearSearch = useCallback(() => {
    setInput("");
    setQuery("");
    setPage(1);
  }, []);

  const searching = query !== "";
  const g = glossary.data;

  return (
    <div data-testid="kb2-browse">
      <section className="kb2-section" aria-labelledby="kb2-glossary-heading">
        <div className="kb2-section-head">
          <h2 className="kb2-section-title" id="kb2-glossary-heading">
            {searching ? "Search results" : "Glossary"}
          </h2>
          {!searching && g && g.products.length > 0 && (
            <select
              className="form-select form-select-sm kb2-product"
              aria-label="Product"
              value={product ?? g.product ?? ""}
              onChange={(e) => setProduct(e.target.value)}
              data-testid="kb2-product"
            >
              {g.products.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.label || p.name} ({p.count})
                </option>
              ))}
            </select>
          )}
          <div className="kb2-search-wrap">
            <input
              type="search"
              className="form-control form-control-sm kb2-search"
              placeholder="Search the knowledge base…"
              aria-label="Search the knowledge base"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="kb2-search"
            />
            {input !== "" && (
              <button
                type="button"
                className="kb2-clear"
                onClick={clearSearch}
                aria-label="Clear search"
                data-testid="kb2-clear"
              >
                &times;
              </button>
            )}
          </div>
        </div>

        {searching ? (
          <>
            {search.loading && !search.data && (
              <p className="kb2-empty" data-testid="kb2-search-loading">{MESSAGES.searchLoading}</p>
            )}
            {search.error && (
              <p className="kb2-empty" data-testid="kb2-search-error">{MESSAGES.searchFailed}</p>
            )}
            {!search.error && search.data && search.data.total === 0 && (
              <p className="kb2-empty" data-testid="kb2-no-results">
                No article matches “{query}”.{" "}
                <button type="button" className="kb2-link" onClick={clearSearch} data-testid="kb2-no-results-clear">
                  Clear the search
                </button>
              </p>
            )}
            {!search.error && search.data && search.data.total > 0 && (
              <>
                <ul className="kb2-results" data-testid="kb2-results">
                  {search.data.results.map((a) => (
                    <ArticleRow key={a.id} article={a} onOpenArticle={onOpenArticle} showTopic />
                  ))}
                </ul>
                <Pager
                  total={search.data.total}
                  page={search.data.page}
                  pageSize={search.data.pageSize || PAGE_SIZE}
                  onPage={setPage}
                />
              </>
            )}
          </>
        ) : (
          <>
            {glossary.loading && !g && (
              <p className="kb2-empty" data-testid="kb2-glossary-loading">{MESSAGES.glossaryLoading}</p>
            )}
            {glossary.error && (
              <p className="kb2-empty" data-testid="kb2-glossary-error">{MESSAGES.glossaryFailed}</p>
            )}
            {!glossary.error && g && g.total === 0 && (
              <p className="kb2-empty" data-testid="kb2-glossary-empty">{MESSAGES.glossaryEmpty}</p>
            )}
            {!glossary.error && g && g.total > 0 && (
              <div className="kb2-glossary" data-testid="kb2-glossary">
                <div className="kb2-glossary-tools">
                  <button
                    type="button"
                    className="kb2-btn"
                    onClick={() => setExpanded(new Set(g.groups.map((group) => group.category)))}
                    data-testid="kb2-expand-all"
                  >
                    Expand all
                  </button>
                  <button
                    type="button"
                    className="kb2-btn"
                    onClick={() => setExpanded(new Set())}
                    data-testid="kb2-collapse-all"
                  >
                    Collapse all
                  </button>
                </div>
                {g.groups.map((group, index) => {
                  const isOpen = expanded.has(group.category);
                  const count = group.subcategories.reduce((n, sub) => n + sub.articles.length, 0);
                  const panelId = `kb2-cat-${index}`;
                  return (
                    <section className={`kb2-cat${isOpen ? " kb2-cat-open" : ""}`} key={group.category} data-testid="kb2-category">
                      <h3 className="kb2-cat-title">
                        <button
                          type="button"
                          className="kb2-cat-toggle"
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                          onClick={() => toggleCategory(group.category)}
                          data-testid="kb2-category-toggle"
                        >
                          <span className="kb2-chevron" aria-hidden="true" />
                          <span className="kb2-cat-name">{group.category}</span>
                          <span className="kb2-cat-count" data-testid="kb2-category-count">{count}</span>
                        </button>
                      </h3>
                      {isOpen && (
                        <div className="kb2-cat-body" id={panelId} data-testid="kb2-category-body">
                          {group.subcategories.map((sub) => (
                            <div className="kb2-sub" key={sub.name ?? "__none"}>
                              {sub.name && <h4 className="kb2-sub-title">{sub.name}</h4>}
                              <ul className="kb2-titles">
                                {sub.articles.map((a) => (
                                  <ArticleRow key={a.id} article={a} onOpenArticle={onOpenArticle} />
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
